/**
 * Flagged quotes (triage). ONLY genuine flags land here (kind=flag) —
 * hand-priced manual quotes never pollute the list (R14: "flagged" means a
 * price looks wrong; manual is intentional).
 */
import { useEffect, useState } from 'preact/hooks';
import { Card, EmptyState } from '@ecmb/ui';
import { listFlaggedQuotes, setFlagStatus, type ApiFlaggedQuote } from '../lib/actions.js';
import { euro } from '../lib/format.js';
import { Banner } from '../components/Banner.js';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; flags: ApiFlaggedQuote[] };

export function Triage() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [correctEuros, setCorrectEuros] = useState('');

  const load = () => {
    listFlaggedQuotes()
      .then((flags) => setState({ kind: 'ready', flags }))
      .catch(() => setState({ kind: 'error' }));
  };
  useEffect(load, []);

  const reject = async (id: number) => {
    setFailure(null);
    if (await setFlagStatus(id, 'rejected')) load();
    else setFailure("That didn't save. Try again in a moment.");
  };

  // Confirming REQUIRES the correct price (the server's feedback contract).
  const confirm = async (id: number) => {
    const euros = Number(correctEuros);
    if (!Number.isFinite(euros) || euros <= 0) {
      setFailure('Enter what the price should have been before confirming.');
      return;
    }
    setFailure(null);
    if (await setFlagStatus(id, 'confirmed', Math.round(euros * 100))) {
      setConfirmingId(null);
      setCorrectEuros('');
      load();
    } else {
      setFailure("That didn't save. Try again in a moment.");
    }
  };

  return (
    <div class="screen-pane">
      <h1 class="screen-title" tabIndex={-1}>Flagged quotes</h1>

      {failure && <Banner kind="error">{failure}</Banner>}
      {/* ⭐ T1749 M9 (DS-25) — `EmptyState state="loading"` (§7.1 FORBIDS A SPINNER
          IN SPACE: the loading state carries a sentence, which is why `headline`
          is required in every state). The sentence is verbatim; what it gains is
          `role="status"`, so "getting the flagged quotes" is ANNOUNCED rather
          than only drawn. */}
      {state.kind === 'loading' && <EmptyState state="loading" headline="Getting the flagged quotes…" />}
      {state.kind === 'error' && (
        <Banner kind="error">
          Loading the flagged quotes failed. Try again in a moment.{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={load}>Try again</button>
        </Banner>
      )}
      {/* ⭐ T1749 M9 (DS-25) — the library's `EmptyState`, sentence verbatim. This
          is a TRUE ZERO with nothing hidden behind a filter, so the default `ok`
          tone (§7.2's good-news green) is the honest one: no flagged quote is
          good news on this screen.
          ⛔ THE SENTENCE IS SPLIT ACROSS THE COMPONENT'S TWO SLOTS, AND THAT IS
          WHAT MAKES THE TWO EMPTIES ONE VOICE (M9 rework 1, ux-review). Passed as
          a single `headline` the explanatory clause rendered at the headline's
          24px/800, while the SAME kind of clause on `QueueView.tsx`'s empty —
          `headline` + `evidence` — renders at 17px/`--ink-600`: one component,
          two voices, on two screens a reader crosses between. The words are
          unchanged; only which slot each clause is in. */}
      {state.kind === 'ready' && state.flags.length === 0 && (
        <EmptyState
          /* ⛔⛔ T1749 M9 rework 2 — `ok` IS ASKED FOR HERE, one of the two sites in
             either tree that asks. THE REASON: this list holds quotes somebody
             marked as LOOKING WRONG, so its emptiness is the absence of a known
             defect — no quote is currently suspected. That is good news about the
             pricing, not about trade. Everywhere the emptiness is merely absence
             (no enquiries, no bookings, no accounts) the default `neutral` stands. */
          tone="ok"
          headline="Nothing flagged"
          evidence="Quotes marked as looking wrong land here for review."
        />
      )}

      {/* ⭐⭐ T1749 M10 (Step 2b) — THE FLAGGED-QUOTE CARD IS THE LIBRARY'S `Card`,
          IN THE `cover` SHAPE, WITH `.enquiry-list` HOLDING THE SPACING. The same
          move `RateChanges.tsx` takes in this step and `Inbox.tsx` took at M8; see
          the list rule in `app.css` for why the 16px and the 720px belong to the
          list rather than to the box (§5.1).
          ⚠️ THIS SCREEN IS UNREACHABLE AND THAT IS RECORDED, NOT ASSUMED. Alex hid
          the `Flagged quotes` rail row at the 2026-09-10 sitting and it was this
          screen's only navigator (`App.tsx`'s `case 'triage':` is the sole
          reference outside this file), so its capture is `test.skip`ped. The
          conversion is still made: a screen nothing can open is the one most
          likely to rot, and leaving it on a retired spelling would be the drift
          the legacy list exists to stop. */}
      {state.kind === 'ready' && (
        <div class="enquiry-list">
          {state.flags.map((f) => (
            <Card key={f.feedback_id} shape="cover">
              <p class="enquiry-title">
                {f.computed_total_cents != null ? euro(f.computed_total_cents) : 'No computed price'}
                {f.created_by ? ` · flagged by ${f.created_by}` : ''}
              </p>
              {f.note && <p class="enquiry-notes">{f.note}</p>}
              {confirmingId === f.feedback_id ? (
                <div class="admin-edit">
                  <label class="field-label" for={`correct-price-${f.feedback_id}`}>
                    What should it have been? (€)
                  </label>
                  <input
                    id={`correct-price-${f.feedback_id}`}
                    class="login-input admin-edit-input"
                    type="number"
                    inputMode="decimal"
                    value={correctEuros}
                    onInput={(e) => setCorrectEuros((e.target as HTMLInputElement).value)}
                  />
                  <button type="button" class="btn btn--secondary btn--inline" onClick={() => void confirm(f.feedback_id)}>
                    Save
                  </button>
                  <button type="button" class="btn btn--quiet btn--inline" onClick={() => setConfirmingId(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div class="action-row action-row--card">
                  <button
                    type="button"
                    class="btn btn--secondary"
                    onClick={() => {
                      setConfirmingId(f.feedback_id);
                      setCorrectEuros('');
                    }}
                  >
                    Confirm the flag
                  </button>
                  <button type="button" class="btn btn--quiet" onClick={() => void reject(f.feedback_id)}>
                    Price was right
                  </button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
